import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { aboutContent, images, companyInfo } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Award, Heart, MapPin, Phone, Mail, Globe, Shield, Beaker, Navigation } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - Compact */}
      <section className="pt-6 pb-12 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.clinicalTrials} alt="About Us" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-12">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              About <span className="text-[#87CEEB]">Us</span>
            </h1>
            <p className="text-base text-white/80 leading-relaxed">
              Pioneering clinical research excellence — delivering breakthrough treatments that transform lives and advance modern medicine.
            </p>
          </div>
        </div>
      </section>

      {/* About Content - Compact */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="relative">
              <img src={images.clinical} alt="Clinical Research" className="rounded-xl shadow-lg w-full h-[250px] object-cover" />
            </div>
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-gray-900">
                Trusted Leaders in <span className="text-[#1877F2]">Clinical Research</span>
              </h2>
              {aboutContent.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 30)} className="text-gray-600 text-sm leading-relaxed">{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Excellence in Research */}
      <section className="py-10 bg-gradient-to-b from-[#F8FBFF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-[#1877F2] font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="text-2xl font-bold text-gray-900 mt-2">
              Excellence in <span className="text-[#1877F2]">Research</span>
            </h2>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-3 gap-4">
            {[
              { icon: Shield, title: 'Safety First', desc: 'Participant safety', id: 'safety' },
              { icon: Beaker, title: 'Innovation', desc: 'Cutting-edge trials', id: 'innovation' },
              { icon: Users, title: 'Expert Team', desc: 'Experienced staff', id: 'expert' }
            ].map((item) => (
              <div key={item.id} className="bg-white border border-[#E6F4FA] rounded-xl p-4 hover:shadow-md transition-all duration-300 group text-center">
                <div className="w-10 h-10 bg-gradient-to-br from-[#1877F2] to-[#87CEEB] rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h3>
                <p className="text-gray-600 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values - Compact */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Target, title: 'Our Mission', desc: aboutContent.mission, id: 'mission' },
              { icon: Users, title: 'Expert Team', desc: 'Dedicated research professionals.', id: 'team' },
              { icon: Award, title: 'Excellence', desc: 'Quality clinical trial studies.', id: 'excellence' },
              { icon: Heart, title: 'Patient Care', desc: 'Utmost respect and care.', id: 'care' }
            ].map((item) => (
              <Card key={item.id} className="border-0 shadow-md hover:shadow-lg transition-all duration-300 rounded-xl">
                <CardContent className="p-4 text-center">
                  <div className="w-10 h-10 bg-[#E6F4FA] rounded-lg flex items-center justify-center mx-auto mb-2">
                    <item.icon className="w-5 h-5 text-[#1877F2]" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h3>
                  <p className="text-gray-600 text-xs leading-relaxed line-clamp-3">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Clinical Trial Process - Small Image Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Our <span className="text-[#1877F2]">Research Process</span></h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Clinical trials follow a rigorous process. Each phase is designed to ensure safety and efficacy before new treatments reach patients.
              </p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_hyde-park-clinic/artifacts/jnq226xz_mapss.jpg" 
              alt="Global Clinical Research"
              className="w-full h-32 object-cover rounded-xl shadow-md"
            />
          </div>
        </div>
      </section>

      {/* Location - Compact with Quick Actions */}
      <section className="py-10 bg-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Visit Our <span className="text-[#1877F2]">Clinic</span>
              </h2>
              <div className="space-y-3 mb-4">
                {[
                  { icon: MapPin, label: 'Address', value: companyInfo.contact.address, id: 'address' },
                  { icon: Phone, label: 'Phone', value: companyInfo.contact.phone, href: `tel:${companyInfo.contact.phone}`, id: 'phone' },
                  { icon: Mail, label: 'Email', value: companyInfo.contact.email, href: `mailto:${companyInfo.contact.email}`, id: 'email' }
                ].map((item) => (
                  <div key={item.id} className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm">
                    <div className="w-9 h-9 bg-[#1877F2] rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-white" />
                    </div>
                    {item.href ? (
                      <a href={item.href} className="font-medium text-gray-900 text-sm hover:text-[#1877F2]">{item.value}</a>
                    ) : (
                      <span className="font-medium text-gray-900 text-sm">{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
              {/* Quick Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <a 
                  href={`tel:${companyInfo.contact.phone}`}
                  className="flex items-center space-x-2 bg-[#1877F2] text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-[#166FE5] transition-all"
                  data-testid="about-call-btn"
                >
                  <Phone size={16} />
                  <span>Call Now</span>
                </a>
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.contact.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-[#E6F4FA] text-[#1877F2] px-4 py-2 rounded-full font-semibold text-sm hover:bg-[#1877F2] hover:text-white transition-all"
                  data-testid="about-directions-btn"
                >
                  <Navigation size={16} />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
            <img src={images.building} alt="Our Clinic" className="rounded-xl shadow-lg w-full h-[220px] object-cover" />
          </div>
        </div>
      </section>

      {/* CTA - Compact */}
      <section className="py-10 bg-gradient-to-r from-[#1877F2] to-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Ready to Make a Difference?</h2>
          <p className="text-white/80 mb-5 text-sm">Join our clinical research programs and contribute to medical advancements.</p>
          <Link to="/join-study" data-testid="about-join-study-btn">
            <Button className="bg-white text-[#1877F2] hover:bg-[#E6F4FA] px-8 py-3 text-base font-bold rounded-full shadow-xl">
              Join a Study Today
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
